<template>
	<transition name="loading-spinner" appear>
		<div class="loading-spinner" ref="spinnerRef">
			<svg
				class="loading-spinner__draw"
				preserveAspectRatio="xMidYMid meet"
				focusable="false"
				:viewBox="`0 0 ${diameter} ${diameter}`"
				ref="drawRef">
				<circle class="loading-spinner__circle" cx="50%" cy="50%" :r="circleRadius" ref="circleRef"></circle>
			</svg>
		</div>
	</transition>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'

const props = defineProps({
	diameter: {
		type: Number,
		default: 42,
	},
	stroke: {
		type: Number,
		default: 4,
	},
})

let circleRadius = computed(() => {
	return (props.diameter - props.stroke) / 2
})

let circleStrokeWidth = computed(() => {
	return `${props.stroke}px`
})

let circleCircumference = computed(() => {
	return 2 * Math.PI * circleRadius.value
})

let circleStrokeDashArray = computed(() => {
	return circleCircumference.value + 'px'
})

let circleStrokeDashOffset = computed(() => {
	return circleCircumference.value * 0.2 + 'px'
})

let drawRef = ref()
let circleRef = ref()
let spinnerRef = ref()
onMounted(() => {
	const svg = drawRef.value
	const size = `${props.diameter}px`
	svg.style.width = size
	svg.style.height = size

	spinnerRef.value.style.width = size
	spinnerRef.value.style.height = size

	const circle = circleRef.value
	circle.style.strokeDasharray = circleStrokeDashArray.value
	circle.style.strokeWidth = circleStrokeWidth.value
	circle.style.setProperty('--md-progress-spinner-start-value', 0.95 * circleCircumference.value)
	circle.style.setProperty('--md-progress-spinner-end-value', 0.2 * circleCircumference.value)
})
</script>

<style scoped lang="scss">
.loading-spinner {
	display: inline-flex;
	position: relative;
	animation: md-progress-spinner-rotate 2s linear infinite;

	&.loading-spinner-enter,
      &.loading-spinner-leave-to {
        .loading-spinner-draw {
          opacity: 0;
          transform: scale(.1);
        }
	  }

	  &.loading-spinner-enter-active,
      &.loading-spinner-leave-active {
        transition-duration: .4s;
        animation: none;
      }

	&__draw {
		color: var(--color--primary);
		// color: modules.color_('primary');
		overflow: visible;
		transform: scale(1) rotate(-90deg);
		transform-origin: center;
		transition: .4s linear;
		will-change: opacity, transform;
	}

	&__circle {
		color: var(--color--primary);
		stroke: var(--color--primary);
		// color: modules.color_('primary');
		// stroke: modules.color_('primary');
		fill: none;
		transform-origin: center;
		transition: stroke-dashoffset .25s linear;

		animation: 4s infinite linear;
        animation-name: md-progress-spinner-stroke-rotate;

	}
}

@keyframes md-progress-spinner-rotate {
    0% {
      transform: rotate(0)
    }
    100% {
      transform: rotate(360deg)
    }
  }
  @keyframes md-progress-spinner-initial-rotate {
    0% {
      opacity: 0;
      transform: rotate(-90deg) translateZ(0);
    }
    20% {
      opacity: 1;
    }
    100% {
      transform: rotate(270deg) translateZ(0);
    }
  }
  @keyframes md-progress-spinner-stroke-rotate-fallback {
    0% {
      transform: rotate(0)
    }
    25% {
      transform: rotate(1170deg)
    }
    50% {
      transform: rotate(2340deg)
    }
    75% {
      transform: rotate(3510deg)
    }
    100% {
      transform: rotate(4680deg)
    }
  }
  @keyframes md-progress-spinner-stroke-rotate {
    0% {
      stroke-dashoffset: var(--md-progress-spinner-start-value);
      transform: rotate(0);
    }
    12.5% {
      stroke-dashoffset: var(--md-progress-spinner-end-value);
      transform: rotate(0);
    }
    12.51% {
      stroke-dashoffset: var(--md-progress-spinner-end-value);
      transform: rotateX(180deg) rotate(72.5deg);
    }
    25% {
      stroke-dashoffset: var(--md-progress-spinner-start-value);
      transform: rotateX(180deg) rotate(72.5deg);
    }
    25.1% {
      stroke-dashoffset: var(--md-progress-spinner-start-value);
      transform: rotate(270deg);
    }
    37.5% {
      stroke-dashoffset: var(--md-progress-spinner-end-value);
      transform: rotate(270deg);
    }
    37.51% {
      stroke-dashoffset: var(--md-progress-spinner-end-value);
      transform: rotateX(180deg) rotate(161.5deg);
    }
    50% {
      stroke-dashoffset: var(--md-progress-spinner-start-value);
      transform: rotateX(180deg) rotate(161.5deg);
    }
    50.01% {
      stroke-dashoffset: var(--md-progress-spinner-start-value);
      transform: rotate(180deg);
    }
    62.5% {
      stroke-dashoffset: var(--md-progress-spinner-end-value);
      transform: rotate(180deg);
    }
    62.51% {
      stroke-dashoffset: var(--md-progress-spinner-end-value);
      transform: rotateX(180deg) rotate(251.5deg);
    }
    75% {
      stroke-dashoffset: var(--md-progress-spinner-start-value);
      transform: rotateX(180deg) rotate(251.5deg);
    }
    75.01% {
      stroke-dashoffset: var(--md-progress-spinner-start-value);
      transform: rotate(90deg);
    }
    87.5% {
      stroke-dashoffset: var(--md-progress-spinner-end-value);
      transform: rotate(90deg);
    }
    87.51% {
      stroke-dashoffset: var(--md-progress-spinner-end-value);
      transform: rotateX(180deg) rotate(341.5deg);
    }
    100% {
      stroke-dashoffset: var(--md-progress-spinner-start-value);
      transform: rotateX(180deg) rotate(341.5deg);
    }
  }
</style>
